// Director types. The director reads snapshots and events only; it never touches scene objects.
import type { CatId } from '../race/types';

export type Vec3 = { x: number; y: number; z: number };

/** What the viewer picked in the HUD. Mirrors hud/store.ts CameraChoice. */
export type CameraChoice = 'director' | 'follow' | 'leader' | 'overview' | 'finish';

export type ShotKind =
  | 'gates' | 'pack' | 'chase' | 'frontQuarter' | 'overview' | 'finishLine'
  | 'catch' | 'tripFollow' | 'landingHigh' | 'ham' | 'straggler' | 'podium'
  | 'duel' | 'photoFinish';

export interface Shot {
  kind: ShotKind;
  subject: CatId | null;
  /** The cat fighting the subject for a place, for the duel shot. */
  rival?: CatId | null;
  /** Higher wins. Event shots interrupt rotation shots; see shots.ts. */
  priority: number;
  /** Plain-words reason, for the debug hook and tests. */
  reason: string;
}

/** A rig's wish for this frame, before smoothing. */
export interface RigPose {
  position: Vec3;
  target: Vec3;
  fov: number;
  /** How fast the camera follows the rig within a shot (1/s). */
  follow: number;
  /** Handheld sway amount, 0 = locked off. */
  handheld: number;
  /**
   * The point the shot travels with, usually the subject cat. The director smooths the camera
   * relative to it, so a fast cat never drags ahead of its camera even at a low frame rate.
   * Absent for shots fixed in the world.
   */
  anchor?: Vec3;
  /** Ease the anchor at this rate (1/s) instead of following it exactly, for anchors that jump. */
  anchorEase?: number;
}

export interface CameraState {
  position: Vec3;
  target: Vec3;
  fov: number;
  shot: ShotKind;
  subject: CatId | null;
  reason: string;
  mode: CameraChoice;
  /** True on the frame a new shot starts with a hard cut. */
  cut: boolean;
  /** True while the director shows the slow-motion finish replay. */
  replay: boolean;
}

export const v = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
